import { Component, Input } from '@angular/core';
import { CurrentUserService } from '@app/core/authentication/current.user';
import { DocumentTemplateLogicService } from '@app/logic/document-template';
import { ILotMappedItem } from '@app/logic/lots';
import { LotPdfReport } from '@app/logic/lots/lot-pdf-report.enum';
import { CbDialogService } from '@app/shared/components/dialog/cb-dialog.service';
import { IPdfViewerDialogItem } from '@app/shared/components/pdf-viewer/pdf-viewer-dialog/pdf-viewer-dialog.component';
import { PdfViewerDialogService } from '@app/shared/components/pdf-viewer/pdf-viewer-dialog/pdf-viewer-dialog.service';
import { DOCUMENT_TEMPLATE_CODES_CONST } from '@classictechsolutions/hubapi-transpiled-enums';
import { LotPermissions } from '@app/core/permissions';
import { take } from 'rxjs';

interface ILotSpecReportMenuItem {
    name: string;
    isVisible?: () => boolean;
    /** Downloaded reports only, a PDF report uses lot.isPdfReportAvailable */
    isDisabled?: () => boolean;
    pdfReport?: LotPdfReport;
    isPdf?: () => boolean;
    download?: () => void;
    confirm?: { heading: string; message: string };
}

@Component({
    selector: 'cb-lot-spec-reports-menu',
    templateUrl: './lot-spec-reports-menu.component.html',
    styleUrls: ['./lot-spec-reports-menu.component.scss'],
    standalone: false
})
export class LotSpecReportsMenuComponent {
    @Input() public readonly lot: ILotMappedItem;

    public readonly reports: ILotSpecReportMenuItem[] = [
        {
            name: 'Draft Create Schedule',
            pdfReport: LotPdfReport.DraftCreateSchedule
        },
        {
            name: 'H&L Flyer',
            isVisible: () => this.lotPermissions.canViewHouseAndLandFlyer() && this.lot.hasHLFlyer,
            pdfReport: LotPdfReport.HLFlyer
        },
        {
            name: 'Color Schedule',
            isDisabled: () => !this.lot.canGenerateColourYourDreams,
            // Only schedule (SIM) lots get a PDF, the other lots get a Word document
            isPdf: () => this.lot.hasScheduleSpecTemplate,
            pdfReport: LotPdfReport.SimColourSchedule,
            download: () => this.lot.generateColourYourDreams().pipe(take(1)).subscribe()
        },
        {
            name: 'Property Information',
            pdfReport: LotPdfReport.PropertyInformation
        },
        {
            name: 'Client Property Information',
            pdfReport: LotPdfReport.ClientPropertyInformation
        },
        {
            name: 'Site Evaluation Form',
            pdfReport: LotPdfReport.SiteEvaluation,
            confirm: {
                heading: 'Generate Site Evaluation',
                message: 'Are you sure you want to generate the Site Evaluation Document'
            }
        },
        {
            name: 'Lot Spec Report',
            pdfReport: LotPdfReport.LotSpecReport
        },
        {
            name: 'CostX Items',
            isVisible: () => this.lot.hasScheduleSpecTemplate,
            isDisabled: () => !this.currentUser.isQSTeam(),
            download: () => this.lot.generateCostXItemsReport().pipe(take(1)).subscribe()
        },
        {
            name: 'Live Schedule',
            isDisabled: () => !this.lot.canGenerateLiveSchedule,
            download: () => this.lot.generateFinalLotSpec().pipe(take(1)).subscribe()
        },
        {
            name: 'Create Book Cover Page I',
            pdfReport: LotPdfReport.CreateBookCoverPage1
        },
        {
            name: 'Create Book Cover Page II',
            pdfReport: LotPdfReport.CreateBookCoverPage2
        },
        {
            name: 'Design and Build Contract Letter',
            isDisabled: () => !this.lot.isDesignAndBuild,
            download: () =>
                this.lot.generateDesignAndBuildContractLetter().pipe(take(1)).subscribe()
        },
        {
            name: 'Pricing Template',
            download: () => this.downloadPricingTemplate()
        }
    ];

    constructor(
        private cbDialog: CbDialogService,
        private readonly documentTemplateLogicService: DocumentTemplateLogicService,
        private readonly pdfViewerDialog: PdfViewerDialogService,
        public readonly currentUser: CurrentUserService,
        public readonly lotPermissions: LotPermissions
    ) {}

    public isVisible(report: ILotSpecReportMenuItem): boolean {
        return report.isVisible ? !!report.isVisible() : true;
    }

    public isDisabled(report: ILotSpecReportMenuItem): boolean {
        if (this.canPreview(report)) {
            return !this.lot.isPdfReportAvailable(report.pdfReport);
        }
        return !!report.isDisabled?.();
    }

    public canPreview(report: ILotSpecReportMenuItem): boolean {
        return report.pdfReport != null && (!report.isPdf || !!report.isPdf());
    }

    public openReport(report: ILotSpecReportMenuItem): void {
        if (!this.canPreview(report)) {
            report.download?.();
            return;
        }
        if (report.confirm) {
            this.cbDialog.confirm({
                dialogHeading: report.confirm.heading,
                message: report.confirm.message,
                confirmed: () => this.openPreview(report)
            });
            return;
        }
        this.openPreview(report);
    }

    public downloadPricingTemplate(): void {
        this.documentTemplateLogicService
            .downloadDocumentTemplate(DOCUMENT_TEMPLATE_CODES_CONST.LotPricingTemplate)
            .pipe(take(1))
            .subscribe();
    }

    private openPreview(selectedReport: ILotSpecReportMenuItem): void {
        const reports = this.reports.filter(
            report => this.isVisible(report) && !this.isDisabled(report) && this.canPreview(report)
        );
        const items: IPdfViewerDialogItem[] = reports.map(report => ({
            id: report.pdfReport,
            name: report.name,
            confirmBeforeLoad: report.confirm
        }));

        this.pdfViewerDialog.open({
            items,
            selectedItemId: selectedReport.pdfReport,
            listTitle: 'Reports',
            loadPdf: (report: LotPdfReport) => this.lot.previewPdfReport(report)
        });
    }
}
